import { Globe, Building2, ShieldCheck, TrendingUp, Ticket, ArrowRight, PlayCircle, BadgeCheck, Star } from "lucide-react";

const STATS = [
  { v: "+5,600", l: "Agencias activas" },
  { v: "50+", l: "Países" },
  { v: "80%", l: "Comisión máxima" },
];

const LINEAS = [
  { Icon: Ticket, t: "Certificados de Viaje", s: "Hasta 80% menos que Booking o Expedia", color: "#80C753" },
  { Icon: ShieldCheck, t: "Asesorías Migratorias", s: "Acompañamos a la familia hasta su visa", color: "#45A0EE" },
  { Icon: Building2, t: "Tu propia Agencia de Viajes", s: "+330 proveedores · +220 mil contratos", color: "#C9A227" },
  { Icon: Globe, t: "Viajemos Travel Club", s: "Precio neto del GDS para la familia", color: "#45A0EE" },
  { Icon: TrendingUp, t: "Programa de Referidos", s: "Gana todos los meses por las personas que invitas", color: "#80C753" },
];

export const HomeHero = () => {
  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden bg-[#060A14] text-white"
    >
      <style>{`
        @keyframes heroUp{0%{opacity:0;transform:translateY(26px)}100%{opacity:1;transform:translateY(0)}}
        @keyframes heroGlow{0%,100%{opacity:.55;transform:scale(1)}50%{opacity:.85;transform:scale(1.08)}}
        @keyframes tickerDot{0%,100%{opacity:.4}50%{opacity:1}}
        @keyframes shine{0%{background-position:0% 50%}100%{background-position:200% 50%}}
        .hero-up{animation:heroUp .8s cubic-bezier(.2,.7,.3,1) both}
        .hero-grad{background:linear-gradient(100deg,#80C753,#45A0EE,#E3C766,#80C753);background-size:200% auto;-webkit-background-clip:text;background-clip:text;color:transparent;animation:shine 7s linear infinite}
      `}</style>

      {/* Background image + overlays */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1483683804023-6ccdb62f86ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Islas tropicales vista aérea"
          className="w-full h-full object-cover opacity-[0.30]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060A14]/70 via-[#060A14]/85 to-[#0B0F19]" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#00153F]/90 via-transparent to-[#0E5C4A]/35" />
      </div>

      {/* Glows */}
      <div className="absolute -top-28 right-[-80px] w-[520px] h-[520px] rounded-full bg-[#45A0EE]/25 blur-[130px]" style={{ animation: "heroGlow 9s ease-in-out infinite" }} />
      <div className="absolute bottom-[-120px] left-[-60px] w-[460px] h-[460px] rounded-full bg-[#80C753]/25 blur-[130px]" style={{ animation: "heroGlow 11s ease-in-out infinite" }} />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
          {/* Left column */}
          <div className="lg:col-span-7">
            {/* Trust ticker pill */}
            <div
              className="hero-up inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md pl-3 pr-4 py-1.5 mb-7"
              data-testid="hero-trust-pill"
              style={{ animationDelay: ".05s" }}
            >
              <span className="w-2 h-2 rounded-full bg-[#80C753]" style={{ animation: "tickerDot 1.8s ease-in-out infinite" }} />
              <span className="text-[11px] sm:text-xs font-semibold tracking-wide text-slate-200">
                Abre tu propia agencia de viajes · <span className="text-white">trabájala desde casa</span>
              </span>
            </div>

            <h1
              className="hero-up font-black tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl mb-5"
              style={{ fontFamily: "'Outfit', sans-serif", animationDelay: ".12s" }}
              data-testid="hero-title"
            >
              Abre tu propia <span className="hero-grad">agencia de viajes</span>
              <span className="block" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 600 }}>
                desde casa.
              </span>
            </h1>

            <p
              className="hero-up text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-6"
              style={{ animationDelay: ".2s" }}
              data-testid="hero-subtitle"
            >
              Crea tu agencia de viajes digital y trabaja desde donde quieras.
              Olvídate de salir a buscar clientes: <span className="text-[#E3C766] font-semibold">nosotros te los enviamos</span>.
              Tú solo vendes experiencias de viaje y ganas comisiones de hasta el 80%.
            </p>

            <p
              className="hero-up text-base sm:text-lg text-white font-semibold leading-relaxed max-w-xl mb-6"
              style={{ animationDelay: ".24s" }}
              data-testid="hero-ownbrand"
            >
              Y lo mejor: te entregamos <span className="text-[#E3C766]">tu propia agencia de viajes, con tu marca y tu logo, lista en un solo día</span>. ✨
            </p>

            {/* Simple chip */}
            <div className="hero-up mb-8" style={{ animationDelay: ".26s" }}>
              <span
                data-testid="hero-simple-chip"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#80C753]/15 border border-[#80C753]/40 px-5 sm:px-6 py-2.5 sm:py-3"
              >
                <BadgeCheck className="h-6 w-6 sm:h-7 sm:w-7 text-[#80C753]" />
                <span className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-white">
                  LO HACEMOS <span className="text-[#80C753]">SIMPLE</span> <span className="text-[#E3C766]">!!</span>
                </span>
              </span>
              <p className="mt-2 text-sm sm:text-base text-slate-300 font-medium">
                Sin complicaciones. Tu agencia lista para vender desde el primer día.
              </p>
            </div>

            {/* Stats */}
            <div className="hero-up grid grid-cols-3 gap-3 sm:gap-4 max-w-lg mb-8" style={{ animationDelay: ".32s" }} data-testid="hero-stats">
              {STATS.map((s) => (
                <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-3 py-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.v}</p>
                  <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-400 mt-1 font-semibold">{s.l}</p>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="hero-up flex flex-col sm:flex-row gap-3 sm:items-center" style={{ animationDelay: ".4s" }}>
              <a
                href="/plandeagencias"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-bold text-[#06210f] bg-gradient-to-r from-[#8FD65F] to-[#80C753] shadow-[0_14px_40px_-10px_rgba(128,199,83,0.6)] hover:shadow-[0_18px_50px_-8px_rgba(128,199,83,0.7)] transition-all hover:-translate-y-0.5"
              >
                Quiero mi agencia
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="/la-historia-en-videos"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-semibold text-white border border-white/20 bg-white/[0.05] backdrop-blur-md hover:bg-white/[0.1] transition-all"
              >
                <PlayCircle className="h-5 w-5 text-[#45A0EE]" />
                Ver la historia
              </a>
            </div>

            {/* License trust line */}
            <div className="hero-up flex items-center gap-2 mt-7 text-[11px] sm:text-xs text-slate-400" style={{ animationDelay: ".48s" }}>
              <Star className="h-3.5 w-3.5 text-[#E3C766] fill-[#E3C766]" />
              Licencia oficial <span className="text-slate-200 font-semibold">Seller of Travel · Florida (FDACS)</span>
              <span className="hidden sm:inline">· Premio Hotmart Keep Growing</span>
            </div>
          </div>

          {/* Right column — 5 business lines glass card */}
          <div className="lg:col-span-5 hero-up" style={{ animationDelay: ".3s" }}>
            <div
              className="relative rounded-3xl border border-white/12 bg-white/[0.055] backdrop-blur-2xl p-6 sm:p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
              data-testid="hero-lineas-card"
            >
              {/* top accent line */}
              <div className="absolute inset-x-6 -top-px h-px bg-gradient-to-r from-transparent via-[#80C753] to-transparent" />
              <div className="flex items-center justify-between mb-5">
                <p className="text-xs font-black uppercase tracking-[2.5px] text-[#80C753]">5 negocios · 1 plataforma</p>
                <span className="text-[10px] font-mono text-slate-500">TRAVEL EMPIRE</span>
              </div>

              <div className="space-y-2.5">
                {LINEAS.map(({ Icon, t, s, color }) => (
                  <div
                    key={t}
                    className="group flex items-center gap-3.5 rounded-2xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.07] px-3.5 py-3 transition-all"
                  >
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${color}1f`, border: `1px solid ${color}44` }}
                    >
                      <Icon className="h-5 w-5" style={{ color }} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white leading-tight">{t}</p>
                      <p className="text-[11px] text-slate-400 leading-snug truncate">{s}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Programa de Referidos explainer */}
              <div className="mt-5 rounded-2xl border border-[#80C753]/25 bg-[#80C753]/[0.08] p-4">
                <p className="text-xs font-black uppercase tracking-wide text-[#9BD872] mb-1.5 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" /> ¿Qué es el Programa de Referidos?
                </p>
                <p className="text-[12.5px] text-slate-300 leading-relaxed">
                  Invita a otras personas a unirse a Club Viajemos. Cuando entran y empiezan a vender,
                  tú ganas una comisión <span className="font-semibold text-white">todos los meses</span> por cada una.
                  Los invitas una vez y sigues cobrando mientras ellos trabajan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
